<template>
    <div class="allocation-timeline">
        <div class="allocation-timeline-heading" aria-live="polite">
            <strong :title="heading">{{ heading }}</strong>
            <span class="allocation-timeline-hint">{{ hint }}</span>
            <span class="allocation-timeline-actions">
                <span class="allocation-timeline-window" :title="'Displayed timeframe: ' + timestamp(start) + ' → ' + timestamp(end)">Graph timeframe</span>
                <v-btn
                    small
                    text
                    color="primary"
                    class="allocation-timeline-zoom-undo"
                    :disabled="!zoomCanUndo"
                    title="Undo the last zoom on this graph"
                    @click="$emit('undo-zoom')">
                    Undo zoom
                </v-btn>
                <v-btn
                    small
                    text
                    color="primary"
                    class="allocation-timeline-zoom-reset"
                    :disabled="!zoomCanReset"
                    title="Reset this graph to the full timeframe"
                    @click="$emit('reset-zoom')">
                    Reset graph
                </v-btn>
                <v-btn v-show="pinnedAt !== null" small text color="primary" class="allocation-timeline-unpin" @click="unpin">Clear pin</v-btn>
                <v-btn v-if="selection && clearSelectionEnabled" small text color="primary" @click="$emit('clear-selection')">Show total</v-btn>
            </span>
        </div>
        <div class="allocation-timeline-warnings" role="status">
            <span v-for="warning in activeWarnings" :key="warning.key">
                <strong>{{ warning.label }}</strong>
                {{ warning.text }}
            </span>
        </div>
        <div class="allocation-timeline-legend">
            <span>
                <i class="allocation-timeline-key allocation-timeline-key--total"></i>
                Total · resource color
            </span>
            <span v-if="selection">
                <i class="allocation-timeline-key allocation-timeline-key--selected" :style="{ borderTopColor: selectionTone }"></i>
                Selected · {{ selection.label }}
            </span>
            <span v-if="hasWorkers" title="Hover a worker reservation-to-limit band or focus its row">
                <i class="allocation-timeline-key allocation-timeline-key--worker"></i>
                Worker traces
            </span>
            <span v-if="hasUnresolved" title="Placement unresolved; this does not establish an unfulfilled request">
                <i class="allocation-timeline-key allocation-timeline-key--unresolved"></i>
                Unresolved placement
            </span>
            <span title="Solid line: recorded reserved value">
                <i class="allocation-timeline-key allocation-timeline-key--reserved"></i>
                R · Reserved
            </span>
            <span title="Dashed line: recorded upper limit">
                <i class="allocation-timeline-key allocation-timeline-key--limit"></i>
                L · Limit
            </span>
            <span title="Dotted line: recorded lower bound; ≥ marks known minimums">
                <i class="allocation-timeline-key allocation-timeline-key--lower"></i>
                Lower bound ≥
            </span>
            <span title="Gray dashed: recorded allocatable capacity at each point">
                <i class="allocation-timeline-key allocation-timeline-key--capacity"></i>
                Capacity
            </span>
            <span title="Amber baseline: intervals with incomplete records">
                <i class="allocation-timeline-key allocation-timeline-key--incomplete"></i>
                Incomplete record
            </span>
            <span class="allocation-timeline-interaction-hint">Hover inspects · drag zooms · click pins</span>
            <details class="allocation-timeline-details">
                <summary>Details</summary>
                <p>
                    Values are recorded reservations and limits, not measured utilization. Partial and uncapped values are lower bounds, not exact totals;
                    capacity prefixed ≥ is a lower bound while some worker lifetimes are unresolved.
                </p>
                <p v-if="contributions.length">
                    Muted traces show recorded contributions. Hover inside an exact reserved-to-limit band or near its boundary, or focus a matching row, to
                    compare it with the pool total. Overlapping bands are ambiguous; partial and uncapped values have no exact band. Unresolved placement does
                    not establish whether requests were fulfilled. A selected class returns when the preview ends.
                </p>
                <p v-for="resource in notedResources" :key="resource.key">{{ resource.label }}: {{ resource.note }}</p>
            </details>
        </div>
        <div class="allocation-timeline-layout">
            <div class="allocation-timeline-labels">
                <div v-for="lane in lanes" :key="lane.resource.key" class="allocation-timeline-label" :style="{ height: laneHeight + 'px' }">
                    <strong :title="lane.resource.note || lane.resource.label">
                        <i :style="{ backgroundColor: lane.color }"></i>
                        <span class="allocation-timeline-resource-name">{{ lane.resource.label }}</span>
                        <span
                            v-if="
                                overcommitted(lane.resource, displayPoint(lane.resource, null)) ||
                                (lane.highlighted && !outsideSelection && overcommitted(lane.resource, displayPoint(lane.resource, lane.highlighted)))
                            "
                            class="allocation-timeline-capacity-warning"
                            title="Budget exceeds recorded capacity"
                            role="img"
                            aria-label="Budget exceeds recorded capacity">
                            <v-icon small color="warning">mdi-alert-circle-outline</v-icon>
                        </span>
                    </strong>
                    <small class="allocation-timeline-unit">{{ lane.resource.unit }}</small>
                    <div class="allocation-timeline-readouts">
                        <span class="allocation-timeline-range-value" :title="'Total · ' + totalLabel(lane)" :aria-label="'Total · ' + totalLabel(lane)">
                            Total · {{ totalValue(lane) }}
                        </span>
                        <span
                            v-if="selection"
                            class="allocation-timeline-range-value allocation-timeline-range-value--selected"
                            :style="{ color: selectionTone }"
                            :title="'Selected · ' + selectedLabel(lane)"
                            :aria-label="'Selected · ' + selectedLabel(lane)">
                            Selected · {{ selectedValue(lane) }}
                        </span>
                    </div>
                    <small
                        class="allocation-timeline-scale"
                        :title="'Plot scale: 0 to ' + measurementText({ value: lane.maximum, status: 'exact' }, lane.resource.unit)">
                        Scale 0–{{ measurementText({ value: lane.maximum, status: 'exact' }) }} {{ lane.resource.unit }}
                    </small>
                    <small
                        v-if="totalCapacityMeasurement(lane.resource)"
                        class="allocation-timeline-hardware"
                        :title="'Hardware ' + measurementText(totalCapacityMeasurement(lane.resource))">
                        Hardware {{ measurementText(totalCapacityMeasurement(lane.resource)) }}
                    </small>
                    <small
                        v-if="capacityMeasurement(lane.resource)"
                        class="allocation-timeline-capacity"
                        :title="'Allocatable ' + measurementText(capacityMeasurement(lane.resource))">
                        Allocatable {{ measurementText(capacityMeasurement(lane.resource)) }}
                    </small>
                </div>
            </div>
            <div class="allocation-timeline-plot">
                <svg
                    ref="plot"
                    :viewBox="`0 0 600 ${chartHeight}`"
                    :style="{ height: chartHeight + 'px' }"
                    preserveAspectRatio="none"
                    role="img"
                    tabindex="0"
                    :aria-label="description"
                    @pointerdown="beginPointer"
                    @pointermove="hoverPointer"
                    @pointerup="endPointer"
                    @pointercancel="cancelPointer"
                    @pointerleave="leavePlot"
                    @focus="focusInspection"
                    @blur="leavePlot"
                    @keydown.left.prevent="moveCursor(-1)"
                    @keydown.right.prevent="moveCursor(1)"
                    @keydown.enter.prevent="pinKeyboard"
                    @keydown.esc.prevent="unpin">
                    <title>{{ description }}</title>
                    <rect
                        v-if="selection"
                        class="allocation-session-interval"
                        :x="chartX(selection.start)"
                        y="0"
                        :width="chartX(selection.end) - chartX(selection.start)"
                        :height="chartHeight"
                        :fill="selectionTone"
                        fill-opacity="0.05" />
                    <g v-for="(lane, index) in lanes" :key="lane.resource.key">
                        <line x1="0" :y1="laneBottom(index)" x2="600" :y2="laneBottom(index)" stroke="#e0e7ef" />
                        <path
                            v-if="capacityPath(lane, index)"
                            class="allocation-timeline-capacity-path"
                            :d="capacityPath(lane, index)"
                            fill="none"
                            stroke="#8391a5"
                            stroke-dasharray="4 4"
                            vector-effect="non-scaling-stroke" />
                        <rect
                            v-for="segment in incompleteSegments(lane.resource.points, index)"
                            :key="'incomplete-' + segment.key"
                            :x="segment.x"
                            :y="segment.y"
                            :width="segment.width"
                            height="3"
                            fill="#b45309"
                            fill-opacity="0.4" />
                        <path :d="rangePath(lane, index)" :fill="lane.color" fill-opacity="0.16" />
                        <path
                            :d="boundaryPath(lane, index, 'requested', lane.resource.points, ['exact'])"
                            fill="none"
                            :stroke="lane.color"
                            stroke-width="2"
                            opacity="1"
                            vector-effect="non-scaling-stroke" />
                        <path
                            :d="boundaryPath(lane, index, 'requested', lane.resource.points, ['partial'])"
                            fill="none"
                            :stroke="lane.color"
                            stroke-width="2"
                            stroke-dasharray="3 3"
                            opacity="1"
                            vector-effect="non-scaling-stroke" />
                        <path
                            :d="boundaryPath(lane, index, 'limit', lane.resource.points, ['exact'])"
                            fill="none"
                            :stroke="lane.color"
                            stroke-width="2"
                            stroke-dasharray="5 3"
                            opacity="1"
                            vector-effect="non-scaling-stroke" />
                        <path
                            :d="boundaryPath(lane, index, 'limit', lane.resource.points, ['partial', 'uncapped'])"
                            fill="none"
                            :stroke="lane.color"
                            stroke-width="2"
                            stroke-dasharray="2 3"
                            opacity="1"
                            vector-effect="non-scaling-stroke" />
                        <g
                            v-for="item in lane.contributions"
                            :key="item.key"
                            :class="item.kind === 'unresolved' ? 'allocation-unresolved-trace' : 'allocation-worker-trace'"
                            pointer-events="none">
                            <path
                                :d="rangePath(lane, index, item.metric.points)"
                                :fill="item.kind === 'unresolved' ? '#aa4315' : '#475569'"
                                :fill-opacity="item.kind === 'unresolved' ? 0.14 : 0.08" />
                            <path
                                :d="boundaryPath(lane, index, 'requested', item.metric.points, ['exact'])"
                                fill="none"
                                :stroke="item.kind === 'unresolved' ? '#aa4315' : '#475569'"
                                stroke-width="1.5"
                                vector-effect="non-scaling-stroke" />
                            <path
                                :d="boundaryPath(lane, index, 'requested', item.metric.points, ['partial'])"
                                fill="none"
                                :stroke="item.kind === 'unresolved' ? '#aa4315' : '#475569'"
                                stroke-width="1.5"
                                stroke-dasharray="2 3"
                                vector-effect="non-scaling-stroke" />
                            <path
                                :d="boundaryPath(lane, index, 'limit', item.metric.points, ['exact'])"
                                fill="none"
                                :stroke="item.kind === 'unresolved' ? '#aa4315' : '#475569'"
                                stroke-width="1.5"
                                stroke-dasharray="5 3"
                                vector-effect="non-scaling-stroke" />
                            <path
                                :d="boundaryPath(lane, index, 'limit', item.metric.points, ['partial', 'uncapped'])"
                                fill="none"
                                :stroke="item.kind === 'unresolved' ? '#aa4315' : '#475569'"
                                stroke-width="1.5"
                                stroke-dasharray="2 3"
                                vector-effect="non-scaling-stroke" />
                        </g>
                        <g v-if="lane.highlighted" class="allocation-session-contribution">
                            <rect
                                v-for="segment in incompleteSegments(lane.highlighted.points, index)"
                                :key="'incomplete-selection-' + segment.key"
                                :x="segment.x"
                                :y="segment.y"
                                :width="segment.width"
                                height="3"
                                fill="#b45309"
                                fill-opacity="0.4" />
                            <path :d="rangePath(lane, index, lane.highlighted.points)" :fill="selectionTone" fill-opacity="0.12" />
                            <path
                                :d="boundaryPath(lane, index, 'requested', lane.highlighted.points, ['exact'])"
                                fill="none"
                                :stroke="selectionTone"
                                stroke-width="3"
                                vector-effect="non-scaling-stroke" />
                            <path
                                :d="boundaryPath(lane, index, 'requested', lane.highlighted.points, ['partial'])"
                                fill="none"
                                :stroke="selectionTone"
                                stroke-width="3"
                                stroke-dasharray="3 3"
                                vector-effect="non-scaling-stroke" />
                            <path
                                :d="boundaryPath(lane, index, 'limit', lane.highlighted.points, ['exact'])"
                                fill="none"
                                :stroke="selectionTone"
                                stroke-width="3"
                                stroke-dasharray="5 3"
                                vector-effect="non-scaling-stroke" />
                            <path
                                :d="boundaryPath(lane, index, 'limit', lane.highlighted.points, ['partial', 'uncapped'])"
                                fill="none"
                                :stroke="selectionTone"
                                stroke-width="3"
                                stroke-dasharray="2 3"
                                vector-effect="non-scaling-stroke" />
                        </g>
                    </g>
                    <rect
                        v-if="brush"
                        class="allocation-timeline-brush"
                        :x="chartX(brush.start)"
                        y="0"
                        :width="Math.max(0, chartX(brush.end) - chartX(brush.start))"
                        :height="chartHeight"
                        fill="#3381ff"
                        fill-opacity="0.14" />
                    <line
                        v-if="effectiveAt !== null && !brush && effectiveAt >= start && effectiveAt <= end"
                        class="allocation-timeline-cursor"
                        :x1="chartX(effectiveAt)"
                        y1="0"
                        :x2="chartX(effectiveAt)"
                        :y2="chartHeight"
                        :stroke="pinnedAt !== null ? '#1d4ed8' : '#64748b'"
                        vector-effect="non-scaling-stroke" />
                </svg>
                <div class="allocation-timeline-time-axis">
                    <span>{{ timestamp(start) }}</span>
                    <span>{{ timestamp(end) }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { formatAllocationTime, formatResourceMeasurement, resourceMeasurementWarning } from './nodeAllocationData'
const colors = ['#3381ff', '#138f89', '#8662cf', '#b27a23', '#32835a', '#52758f']
const exactBoundaries = ['exact']
const brushThresholdPixels = 6
const cursorStepFraction = 0.01

export default {
    name: 'NodeAllocationTimeline',
    props: {
        start: { type: Number, required: true },
        end: { type: Number, required: true },
        resources: { type: Array, required: true },
        selection: { type: Object, default: null },
        contributions: { type: Array, default: () => [] },
        hoveredContributionKey: { type: String, default: null },
        clearSelectionEnabled: { type: Boolean, default: true },
        pinnedAt: { type: Number, default: null },
        zoomCanUndo: { type: Boolean, default: false },
        zoomCanReset: { type: Boolean, default: false }
    },
    data() {
        return { cursor: null, laneHeight: 128, drag: null, brush: null }
    },
    created() {
        this.pointerHandlers = null
    },
    computed: {
        // The pinned timestamp is controlled by the parent and survives pointer
        // leave and blur; transient hover/keyboard inspection only fills the gap.
        effectiveAt() {
            return this.pinnedAt === null || this.pinnedAt === undefined ? this.cursor : this.pinnedAt
        },
        outsideSelection() {
            return Boolean(this.selection && this.effectiveAt !== null && (this.effectiveAt < this.selection.start || this.effectiveAt >= this.selection.end))
        },
        hasWorkers() {
            return this.contributions.some(item => item.kind === 'worker')
        },
        hasUnresolved() {
            return this.contributions.some(item => item.kind === 'unresolved')
        },
        selectionTone() {
            return this.selection?.kind === 'unresolved' ? '#aa4315' : '#a6326d'
        },
        lanes() {
            return this.resources.map((resource, index) => {
                const highlighted = this.selection?.resources.find(item => item.key === resource.key) || null
                const matched = []
                for (const item of this.contributions) {
                    const metric = item.resources.find(candidate => candidate.key === resource.key)
                    if (metric) matched.push({ key: item.key, kind: item.kind, metric })
                }
                let maximum = 0
                const consider = value => {
                    if (typeof value === 'number' && Number.isFinite(value) && value > maximum) maximum = value
                }
                consider(resource.capacity)
                for (const point of resource.points || []) {
                    consider(point.requested)
                    consider(point.limit)
                    consider(point.capacity)
                }
                if (highlighted) {
                    consider(highlighted.requested)
                    consider(highlighted.limit)
                    for (const point of highlighted.points || []) {
                        consider(point.requested)
                        consider(point.limit)
                    }
                }
                for (const item of matched) {
                    for (const point of item.metric.points) {
                        consider(point.requested)
                        consider(point.limit)
                    }
                }
                return {
                    resource,
                    highlighted,
                    contributions: matched,
                    color: colors[index % colors.length],
                    maximum: maximum || 1
                }
            })
        },
        chartHeight() {
            return this.lanes.length * this.laneHeight
        },
        heading() {
            const at = this.effectiveAt
            if (this.selection) return `${this.selection.label} vs total · ${at === null ? 'recorded peaks' : this.timestamp(at)}`
            return at === null ? 'Known peak totals' : this.timestamp(at)
        },
        hint() {
            if (this.pinnedAt !== null) return 'Pinned · Esc unpins'
            if (this.outsideSelection) return 'Selection inactive · total still shown'
            if (this.selection) return 'Same scale · compare selected with total'
            return this.effectiveAt === null ? 'Hover, drag or focus to inspect' : 'Reserved and limit at one timestamp'
        },
        activeWarnings() {
            const warnings = []
            for (const lane of this.lanes) {
                const series = [{ point: this.displayPoint(lane.resource, null), label: lane.resource.label, key: '' }]
                if (lane.highlighted && !this.outsideSelection) {
                    series.push({ point: this.displayPoint(lane.resource, lane.highlighted), label: `${lane.resource.label} · selected`, key: '-selected' })
                }
                for (const entry of series) {
                    if (!entry.point) continue
                    for (const kind of ['requests', 'limits']) {
                        const warning = resourceMeasurementWarning(this.measurement(entry.point, kind), kind)
                        if (!warning) continue
                        warnings.push({ key: `${lane.resource.key}${entry.key}-${kind}-${warning}`, label: entry.label, text: warning })
                    }
                    if (this.overcommitted(lane.resource, entry.point)) {
                        warnings.push({ key: `${lane.resource.key}${entry.key}-capacity`, label: entry.label, text: 'Budget exceeds recorded capacity' })
                    }
                }
            }
            return warnings
        },
        notedResources() {
            return this.resources.filter(resource => resource.note)
        },
        description() {
            return (
                'Node allocation on a shared UTC time axis. Each resource has a labeled zero-to-maximum scale and shows its total recorded reserved and limit values. A selected application, class, worker or unresolved-placement session is drawn in a contrasting color on the same scale as its total; without inspection, the two readouts are independent recorded peaks, not necessarily simultaneous. Solid: reserved. Dashed: limit. Dotted: partial or uncapped lower bound. Amber baseline: incomplete records. Gray dashed: recorded allocatable capacity. Values are reservations and limits, not measured utilization or proof of fulfillment. Drag to zoom this graph, click or press Enter to pin inspection, Escape to unpin, and use left and right arrows to inspect all resources at one time. Undo zoom reverses the last zoom on this graph; Reset graph restores its full timeframe.' +
                (this.contributions.length
                    ? ' Muted traces show individual contributions. Hover inside an exact reservation-to-limit band or near a boundary to highlight its row; overlapping bands do not identify a single session.'
                    : '')
            )
        }
    },
    methods: {
        timestamp: formatAllocationTime,
        measurement(point, kind) {
            const requestSide = kind === 'requests' || kind === 'requested'
            const value = point ? point[requestSide ? 'requested' : 'limit'] : null
            const status = point ? point[requestSide ? 'requestStatus' : 'limitStatus'] : null
            if (status) return { value: value === undefined ? null : value, status }
            return { value: value === undefined ? null : value, status: value === null || value === undefined ? 'unrecorded' : 'exact' }
        },
        measurementText(measurement, unit) {
            return formatResourceMeasurement(measurement, unit)
        },
        rangeText(resource, point) {
            if (!point) return 'Not recorded'
            const requests = this.measurement(point, 'requests')
            const limits = this.measurement(point, 'limits')
            if (requests.status === 'unrecorded' && limits.status === 'unrecorded') return 'Not recorded'
            return `Reserved: ${this.measurementText(requests, resource.unit)} · Limit: ${this.measurementText(limits, resource.unit)}`
        },
        compactRange(point) {
            if (!point) return 'Not recorded'
            const requests = this.measurement(point, 'requests')
            const limits = this.measurement(point, 'limits')
            if (requests.status === 'unrecorded' && limits.status === 'unrecorded') return 'Not recorded'
            return `R ${this.measurementText(requests)} / L ${this.measurementText(limits)}`
        },
        totalValue(lane) {
            return this.compactRange(this.displayPoint(lane.resource, null))
        },
        selectedValue(lane) {
            if (this.outsideSelection) return 'Outside interval'
            return this.compactRange(lane.highlighted ? this.displayPoint(lane.resource, lane.highlighted) : null)
        },
        totalLabel(lane) {
            return this.rangeText(lane.resource, this.displayPoint(lane.resource, null))
        },
        selectedLabel(lane) {
            if (this.outsideSelection) return 'Outside selected interval'
            if (!lane.highlighted) return 'Not recorded'
            return this.rangeText(lane.highlighted, this.displayPoint(lane.resource, lane.highlighted))
        },
        pointMagnitude(point, kind) {
            const value = point?.[kind]
            return value === null || value === undefined ? null : value
        },
        // Series include the window endpoints; the final point is not covered by
        // any half-open interval, so peaks are taken from the active points only.
        peakPoint(points, preferLimits) {
            if (!Array.isArray(points) || points.length === 0) return null
            let peak = null
            for (let index = 0; index < points.length - 1; index++) {
                const point = points[index]
                if (preferLimits) {
                    if (this.pointMagnitude(point, 'limit') === null) continue
                    if (!peak || point.limit > peak.limit || (point.limit === peak.limit && this.segmentIncomplete(point) && !this.segmentIncomplete(peak)))
                        peak = point
                } else {
                    if (this.pointMagnitude(point, 'requested') === null) continue
                    if (
                        !peak ||
                        point.requested > peak.requested ||
                        (point.requested === peak.requested && this.segmentIncomplete(point) && !this.segmentIncomplete(peak))
                    )
                        peak = point
                }
            }
            return peak
        },
        pointAt(points, at) {
            if (!Array.isArray(points) || !points.length || at === null || at === undefined) return null
            if (at < points[0].at || at >= points[points.length - 1].at) return null
            let left = 0
            let right = points.length - 1
            while (left + 1 < right) {
                const middle = Math.floor((left + right) / 2)
                if (points[middle].at <= at) left = middle
                else right = middle
            }
            return points[left]
        },
        // Without a cursor, each series reports its own recorded peak; these
        // peaks need not have occurred simultaneously. With a cursor both
        // values refer to the same timestamp on the same resource scale.
        displayPoint(resource, series = this.selection?.resources.find(item => item.key === resource.key) || null) {
            const source = series || resource
            const points = Array.isArray(source.points) ? source.points : []
            const at = this.effectiveAt
            if (at === null) {
                if (source.requested === null && source.limit === null) {
                    return points.slice(0, -1).find(point => this.segmentIncomplete(point)) || null
                }
                return this.peakPoint(points, this.pointMagnitude(source, 'limit') !== null)
            }
            return this.pointAt(points, at)
        },
        seriesHasField(points, field) {
            return Array.isArray(points) && points.some(point => point && point[field] !== undefined)
        },
        // Capacity always reads the lane's own series so highlighted classes use
        // the recorded pool capacity reference, never a per-class capacity. An
        // explicit `…Status` marks partial known-worker capacity as a lower
        // bound; an unrecorded pool point states that capacity is not recorded
        // instead of plotting a zero line. Node series without per-point fields
        // keep the constant fallback and hide the caption when it is missing.
        seriesCapacityMeasurement(resource, field, at) {
            const points = resource && Array.isArray(resource.points) ? resource.points : []
            const status = value => {
                const recorded = value[`${field}Status`]
                return typeof recorded === 'string' ? recorded : 'exact'
            }
            if (!this.seriesHasField(points, field)) {
                if (!resource || typeof resource[field] !== 'number') return null
                return { value: resource[field], status: status(resource) }
            }
            if (at === null || at === undefined) {
                if (typeof resource[field] === 'number') return { value: resource[field], status: status(resource) }
                return { value: null, status: resource && typeof resource[`${field}Status`] === 'string' ? resource[`${field}Status`] : 'unrecorded' }
            }
            const point = this.pointAt(points, at)
            if (!point || typeof point[field] !== 'number') return { value: null, status: point ? status(point) : 'unrecorded' }
            return { value: point[field], status: status(point) }
        },
        capacityMeasurement(resource, at = this.effectiveAt) {
            const referenceAt = at === null ? this.displayPoint(resource, null)?.at ?? null : at
            return this.seriesCapacityMeasurement(resource, 'capacity', referenceAt)
        },
        totalCapacityMeasurement(resource, at = this.effectiveAt) {
            const referenceAt = at === null ? this.displayPoint(resource, null)?.at ?? null : at
            return this.seriesCapacityMeasurement(resource, 'totalCapacity', referenceAt)
        },
        // Only exact capacity proves the upper limit is above allocatable
        // capacity; a partial lower bound cannot establish overcommitment.
        overcommitted(resource, point) {
            if (!point) return false
            const capacity = this.capacityMeasurement(resource, point.at !== undefined ? point.at : this.effectiveAt)
            if (!capacity || capacity.status !== 'exact') return false
            return point.limit !== null && point.limit !== undefined && point.limit > capacity.value
        },
        chartX(at) {
            return (600 * (at - this.start)) / (this.end - this.start)
        },
        laneBottom(index) {
            return (index + 1) * this.laneHeight - 12
        },
        chartY(value, lane, index) {
            return this.laneBottom(index) - ((this.laneHeight - 28) * value) / lane.maximum
        },
        // Records the allocatable capacity reference: the lane constant for node
        // series, or a step following every recorded per-point pool capacity.
        capacityPath(lane, index) {
            const points = lane.resource && Array.isArray(lane.resource.points) ? lane.resource.points : []
            if (!this.seriesHasField(points, 'capacity')) {
                if (typeof lane.resource.capacity !== 'number') return ''
                return `M0,${this.chartY(lane.resource.capacity, lane, index)}H600`
            }
            const parts = []
            let drawing = false
            for (let position = 0; position < points.length; position++) {
                const value = points[position].capacity
                if (typeof value !== 'number') {
                    drawing = false
                    continue
                }
                const y = this.chartY(value, lane, index)
                parts.push(drawing ? `V${y}` : `M${this.chartX(points[position].at)},${y}`)
                drawing = true
                const next = position + 1 < points.length ? points[position + 1].at : this.end
                parts.push(`H${this.chartX(next)}`)
            }
            return parts.join(' ')
        },
        boundedSegment(point) {
            return (
                this.measurement(point, 'requests').status === 'exact' &&
                this.measurement(point, 'limits').status === 'exact' &&
                point.requested !== null &&
                point.requested !== undefined &&
                point.limit !== null &&
                point.limit !== undefined
            )
        },
        rangePath(lane, index, points = lane.resource.points) {
            const parts = []
            for (let i = 0; i < points.length - 1; i++) {
                const point = points[i]
                if (!this.boundedSegment(point)) continue
                const left = this.chartX(point.at)
                const right = this.chartX(points[i + 1].at)
                const lower = this.chartY(point.requested, lane, index)
                const upper = this.chartY(point.limit, lane, index)
                parts.push(`M${left},${lower}H${right}V${upper}H${left}Z`)
            }
            return parts.join(' ')
        },
        boundaryPath(lane, index, kind, points = lane.resource.points, statuses = exactBoundaries) {
            const parts = []
            for (let i = 0; i < points.length - 1; i++) {
                const point = points[i]
                const value = point[kind]
                if (value === null || value === undefined) continue
                if (statuses && !statuses.includes(this.measurement(point, kind === 'requested' ? 'requests' : 'limits').status)) continue
                parts.push(`M${this.chartX(point.at)},${this.chartY(value, lane, index)}H${this.chartX(points[i + 1].at)}`)
                const next = points[i + 1][kind]
                if (next !== null && next !== undefined) parts.push(`V${this.chartY(next, lane, index)}`)
            }
            return parts.join(' ')
        },
        incompleteSegments(points = [], index = 0) {
            const segments = []
            for (let i = 0; i < points.length - 1; i++) {
                if (!this.segmentIncomplete(points[i])) continue
                segments.push({
                    key: `${points[i].at}-${i}`,
                    x: this.chartX(points[i].at),
                    width: Math.max(0, this.chartX(points[i + 1].at) - this.chartX(points[i].at)),
                    y: this.laneBottom(index) - 3
                })
            }
            return segments
        },
        segmentIncomplete(point) {
            const requests = this.measurement(point, 'requests').status
            const limits = this.measurement(point, 'limits').status
            return requests !== 'exact' || limits !== 'exact'
        },
        setInspection(at) {
            if (typeof at !== 'number' || !Number.isFinite(at)) return
            this.cursor = at
            this.$emit('inspect', at)
        },
        timeFromPointer(event) {
            const element = this.$refs.plot
            if (!element || typeof element.getBoundingClientRect !== 'function') return null
            if (!event || typeof event.clientX !== 'number' || !Number.isFinite(event.clientX)) return null
            const box = element.getBoundingClientRect()
            if (!box || !Number.isFinite(box.width) || box.width <= 0) return null
            const span = this.end - this.start
            if (!Number.isFinite(span) || span <= 0) return null
            const fraction = Math.max(0, Math.min(1, (event.clientX - box.left) / box.width))
            return Math.max(this.start, Math.min(this.end, Math.round(this.start + fraction * span)))
        },
        inspectPointer(event) {
            const at = this.timeFromPointer(event)
            if (at === null) return
            this.setInspection(Math.min(at, this.end - 1))
        },
        hoverPointer(event) {
            if (this.drag) return
            this.inspectPointer(event)
            this.inspectContributionPointer(event)
        },
        inspectContributionPointer(event) {
            if (!this.contributions.length || !Number.isFinite(event?.clientY)) return
            const element = this.$refs.plot
            const box = element?.getBoundingClientRect()
            const at = this.timeFromPointer(event)
            if (!box || !(box.height > 0) || at === null) return
            const y = ((event.clientY - box.top) * this.chartHeight) / box.height
            const index = Math.floor(y / this.laneHeight)
            const lane = this.lanes[index]
            const inspectedAt = Math.min(at, this.end - 1)
            let bestKey = null
            let best = Infinity
            let second = Infinity
            let bandKey = null
            let bands = 0
            if (lane)
                for (const item of lane.contributions) {
                    const point = this.pointAt(item.metric.points, inspectedAt)
                    if (!point) continue
                    const requested = point.requested
                    const limit = point.limit
                    const validRequest = typeof requested === 'number' && Number.isFinite(requested)
                    const validLimit = typeof limit === 'number' && Number.isFinite(limit)
                    const requestY = validRequest ? this.chartY(requested, lane, index) : null
                    const limitY = validLimit ? this.chartY(limit, lane, index) : null
                    if (
                        validRequest &&
                        validLimit &&
                        limit >= requested &&
                        this.measurement(point, 'requests').status === 'exact' &&
                        this.measurement(point, 'limits').status === 'exact' &&
                        y >= limitY &&
                        y <= requestY
                    ) {
                        bandKey = item.key
                        bands++
                    }
                    const distance = Math.min(requestY === null ? Infinity : Math.abs(y - requestY), limitY === null ? Infinity : Math.abs(y - limitY))
                    if (!Number.isFinite(distance)) continue
                    if (distance < best) {
                        second = best
                        best = distance
                        bestKey = item.key
                    } else if (distance < second) second = distance
                }
            const pixel = this.chartHeight / box.height
            const nearDistinctLine = second - best > 2 * pixel
            const key =
                best <= 3 * pixel && nearDistinctLine ? bestKey : bands === 1 ? bandKey : bands === 0 && best <= 9 * pixel && nearDistinctLine ? bestKey : null
            if (key !== this.hoveredContributionKey) this.$emit('hover-contribution', key)
        },
        leavePlot() {
            this.clearInspection()
            if (this.contributions.length) this.$emit('hover-contribution', null)
        },
        beginPointer(event) {
            if (this.drag || event?.isPrimary === false) return
            if (!event || typeof event.clientX !== 'number') return
            if (typeof event.button === 'number' && event.button > 0) return
            const at = this.timeFromPointer(event)
            if (this.contributions.length) this.$emit('hover-contribution', null)
            if (at === null) return
            this.drag = {
                pointerId: typeof event.pointerId === 'number' ? event.pointerId : null,
                downAt: at,
                downX: event.clientX,
                brushing: false
            }
            this.capturePointer(event)
            this.addWindowPointerListeners()
        },
        updateDrag(event) {
            if (!this.drag || !event) return
            if (this.drag.pointerId !== null && typeof event.pointerId === 'number' && event.pointerId !== this.drag.pointerId) return
            const at = this.timeFromPointer(event)
            if (at === null) return
            if (!this.drag.brushing && typeof event.clientX === 'number' && Math.abs(event.clientX - this.drag.downX) >= brushThresholdPixels) {
                this.drag.brushing = true
            }
            if (!this.drag.brushing) {
                this.setInspection(Math.min(at, this.end - 1))
                return
            }
            this.brush = { start: Math.min(this.drag.downAt, at), end: Math.max(this.drag.downAt, at) }
        },
        endPointer(event) {
            if (!this.drag) return
            if (this.drag.pointerId !== null && typeof event?.pointerId === 'number' && event.pointerId !== this.drag.pointerId) return
            this.updateDrag(event)
            const drag = this.drag
            const brush = this.brush
            this.releasePointer(event)
            this.removeWindowPointerListeners()
            this.drag = null
            this.brush = null
            if (drag.brushing && brush) {
                // A brush zooms the half-open window it covers; it never pins.
                const zoom = this.clampedZoom(brush.start, brush.end)
                if (zoom && (zoom.start !== this.start || zoom.end !== this.end)) this.$emit('zoom', zoom)
                this.clearInspection()
                return
            }
            const at = Math.min(drag.downAt, this.end - 1)
            this.setInspection(at)
            this.$emit('pin', at)
        },
        cancelPointer(event) {
            if (!this.drag) return
            if (this.drag.pointerId !== null && typeof event?.pointerId === 'number' && event.pointerId !== this.drag.pointerId) return
            this.releasePointer(event)
            this.removeWindowPointerListeners()
            this.drag = null
            this.brush = null
        },
        clampedZoom(from, to) {
            const fromAt = Math.max(this.start, Math.min(this.end, Math.round(from)))
            const toAt = Math.max(this.start, Math.min(this.end, Math.round(to)))
            if (!Number.isFinite(fromAt) || !Number.isFinite(toAt) || toAt <= fromAt) return null
            return { start: fromAt, end: toAt }
        },
        capturePointer(event) {
            const element = this.$refs.plot
            if (!element || typeof event.pointerId !== 'number' || typeof element.setPointerCapture !== 'function') return
            try {
                element.setPointerCapture(event.pointerId)
            } catch (_) {
                // Pointer capture is best-effort; the window listeners still track the drag.
            }
        },
        releasePointer(event) {
            const element = this.$refs.plot
            const pointerId = this.drag && typeof this.drag.pointerId === 'number' ? this.drag.pointerId : null
            if (!element || pointerId === null || typeof element.releasePointerCapture !== 'function') return
            try {
                element.releasePointerCapture(pointerId)
            } catch (_) {
                // Capture may already be released.
            }
        },
        addWindowPointerListeners() {
            if (this.pointerHandlers) return
            this.pointerHandlers = {
                pointermove: event => this.updateDrag(event),
                pointerup: event => this.endPointer(event),
                pointercancel: event => this.cancelPointer(event)
            }
            for (const type of Object.keys(this.pointerHandlers)) window.addEventListener(type, this.pointerHandlers[type])
        },
        removeWindowPointerListeners() {
            if (!this.pointerHandlers) return
            for (const type of Object.keys(this.pointerHandlers)) window.removeEventListener(type, this.pointerHandlers[type])
            this.pointerHandlers = null
        },
        moveCursor(direction) {
            const current = this.effectiveAt === null ? this.start : this.effectiveAt
            const at = Math.max(this.start, Math.min(this.end - 1, Math.round(current + direction * (this.end - this.start) * cursorStepFraction)))
            this.setInspection(at)
            if (this.pinnedAt !== null && at !== this.pinnedAt) this.$emit('pin', at)
        },
        pinKeyboard() {
            const at = this.effectiveAt === null ? this.end - 1 : this.effectiveAt
            if (this.pinnedAt !== null && at === this.pinnedAt) return
            this.setInspection(at)
            this.$emit('pin', at)
        },
        unpin() {
            this.clearInspection()
            this.$emit('pin', null)
        },
        clearInspection() {
            this.cursor = null
            this.$emit('inspect', null)
        },
        focusInspection() {
            this.setInspection(this.selection ? this.selection.start : this.end - 1)
        }
    },
    watch: {
        start() {
            this.cancelPointer()
            this.cursor = null
            this.brush = null
        },
        end() {
            this.cancelPointer()
            this.cursor = null
            this.brush = null
        },
        resources() {
            this.cursor = null
        },
        selection(next, previous) {
            if (this.contributions.some(item => item.key === next?.key || item.key === previous?.key)) return
            this.clearInspection()
        }
    },
    beforeDestroy() {
        this.cancelPointer()
        this.removeWindowPointerListeners()
    }
}
</script>

<style scoped>
.allocation-timeline {
    padding: 16px 20px;
    border-top: 1px solid #e0e7ef;
    border-bottom: 1px solid #e0e7ef;
}
.allocation-timeline-heading {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
    align-items: center;
    height: 36px;
    gap: 12px;
    color: #64748b;
    font-size: 11px;
    margin-bottom: 8px;
}
.allocation-timeline-heading strong {
    font-weight: 500;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.allocation-timeline-hint {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.allocation-timeline-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    margin-left: auto;
}
.allocation-timeline-window {
    flex-shrink: 0;
    white-space: nowrap;
    color: #64748b;
}
/* Fixed-height slot: warnings can appear or disappear without moving the chart. */
.allocation-timeline-warnings {
    display: flex;
    flex-direction: column;
    gap: 3px;
    height: 32px;
    overflow-y: auto;
    font-size: 10px;
    color: #95621c;
}
.allocation-timeline-warnings strong {
    font-weight: 500;
}
.allocation-timeline-legend {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 14px;
    margin-bottom: 10px;
    font-size: 10px;
    color: #64748b;
}
.allocation-timeline-legend .allocation-timeline-key {
    display: inline-block;
    width: 16px;
    height: 0;
    margin-right: 4px;
    vertical-align: middle;
    border-top-width: 2px;
}
.allocation-timeline-legend .allocation-timeline-key--total {
    height: 3px;
    border: 0;
    background: linear-gradient(90deg, #3381ff, #138f89, #8662cf, #32835a);
}
.allocation-timeline-key--selected {
    border-top-style: solid;
    border-top-color: #a6326d;
}
.allocation-timeline-key--reserved {
    border-top-style: solid;
    border-top-color: #3381ff;
}
.allocation-timeline-key--limit {
    border-top-style: dashed;
    border-top-color: #3381ff;
}
.allocation-timeline-key--worker {
    border-top-style: solid;
    border-top-color: #475569;
    opacity: 0.5;
}
.allocation-timeline-key--unresolved {
    border-top-style: solid;
    border-top-color: #aa4315;
    opacity: 0.8;
}
.allocation-timeline-key--lower {
    border-top-style: dotted;
    border-top-color: #8a94a6;
}
.allocation-timeline-key--capacity {
    border-top-style: dashed;
    border-top-color: #8391a5;
}
.allocation-timeline-key--incomplete {
    height: 3px;
    border-top: 0;
    background: #b45309;
    opacity: 0.5;
}
.allocation-timeline-interaction-hint {
    color: #8a94a6;
}
.allocation-timeline-details summary {
    cursor: pointer;
    color: #64748b;
}
.allocation-timeline-details p {
    margin: 4px 0 0;
    max-width: 640px;
    line-height: 1.6;
}
.allocation-timeline-layout {
    display: grid;
    grid-template-columns: minmax(190px, 250px) minmax(160px, 1fr);
    gap: 20px;
}
.allocation-timeline-label {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 2px;
    min-width: 0;
    overflow: hidden;
}
.allocation-timeline-label strong {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    line-height: 18px;
    min-width: 0;
}
.allocation-timeline-label strong > i {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
}
.allocation-timeline-readouts {
    width: 100%;
    min-width: 0;
}
.allocation-timeline-range-value--selected {
    color: #a6326d;
    font-weight: 600;
}
.allocation-timeline-range-value {
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    line-height: 15px;
    display: block;
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.allocation-timeline-label small {
    color: #64748b;
    font-size: 10px;
    line-height: 12px;
    display: block;
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.allocation-timeline-resource-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.allocation-timeline-capacity-warning {
    flex-shrink: 0;
    line-height: 16px;
}
.allocation-timeline-label .allocation-timeline-hardware {
    color: #475569;
}
.allocation-worker-trace {
    opacity: 0.42;
}
.allocation-unresolved-trace {
    opacity: 0.7;
}
.allocation-timeline svg {
    display: block;
    width: 100%;
    touch-action: none;
    user-select: none;
    cursor: crosshair;
}
.allocation-timeline svg:focus-visible {
    outline: 2px solid #3381ff;
    outline-offset: 4px;
}
.allocation-timeline-time-axis {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 4px;
    padding-top: 8px;
    font-size: 10px;
    color: #64748b;
}
@media (max-width: 720px) {
    .allocation-timeline-heading {
        grid-template-columns: minmax(0, 1fr);
        grid-template-rows: 30px 36px;
        height: 66px;
        gap: 0;
    }
    .allocation-timeline-hint,
    .allocation-timeline-window {
        display: none;
    }
    .allocation-timeline-actions {
        width: 100%;
        min-width: 0;
        margin-left: 0;
        justify-content: flex-start;
        overflow-x: auto;
    }
    .allocation-timeline {
        overflow-x: auto;
    }
}
@media (max-width: 600px) {
    .allocation-timeline {
        padding: 12px;
        overflow-x: auto;
    }
    .allocation-timeline-layout {
        min-width: 460px;
        grid-template-columns: 190px minmax(240px, 1fr);
        gap: 12px;
    }
}
</style>
